/**
 * The social product's reads (lane ALGOBOO2): the front page, shadows, remixes, the daily story, the team an agent
 * is and challenges. Every figure is the platform's, passed through the fail-closed validation of client.ts.
 */
import { queryOptions, useQuery } from '@tanstack/react-query'
import { request } from '../client'

export const productKeys = {
  front: () => ['front'] as const,
  shadows: () => ['me', 'shadows'] as const,
  shadow: (ref: string) => ['me', 'shadows', ref] as const,
  remix: (handle: string) => ['agent', handle, 'remix'] as const,
  lineage: (handle: string) => ['agent', handle, 'lineage'] as const,
  story: (handle: string, day: string) => ['agent', handle, 'story', day] as const,
  team: (handle: string) => ['agent', handle, 'team'] as const,
  challenges: () => ['me', 'challenges'] as const,
  challenge: (id: string) => ['challenge', id] as const,
}

export const frontQuery = () => queryOptions({ queryKey: productKeys.front(), queryFn: ({ signal }) => request('getFront', { signal }), refetchInterval: 30_000 })

/** `GET /front`: today's ranking, the tokens, the gainers, the launches and the live trades (the API caches 30 s). */
export function useFront() {
  return useQuery(frontQuery())
}

export const myShadowsQuery = () => queryOptions({ queryKey: productKeys.shadows(), queryFn: ({ signal }) => request('listMyShadows', { signal }) })

/** `GET /me/shadows`: the user's shadows, their terms. */
export function useMyShadows(enabled = true) {
  return useQuery({ ...myShadowsQuery(), enabled })
}

/** `GET /me/shadows/:ref`: one with what it would have made, simulated by the platform now. */
export function useMyShadow(ref: string | undefined) {
  return useQuery({ queryKey: productKeys.shadow(ref ?? ''), queryFn: ({ signal }) => request('getMyShadow', { signal, params: { ref: ref! } }), enabled: !!ref, refetchInterval: 60_000 })
}

export const remixOptionsQuery = (handle: string) => queryOptions({ queryKey: productKeys.remix(handle), queryFn: ({ signal }) => request('getRemixOptions', { signal, params: { handle } }) })

/** `GET /agents/:handle/remix`: the plain-words knobs the agent's program allows. */
export function useRemixOptions(handle: string) {
  return useQuery(remixOptionsQuery(handle))
}

/** `GET /agents/:handle/lineage`: where it came from, its remixes, both results since the fork. */
export function useAgentLineage(handle: string) {
  return useQuery({ queryKey: productKeys.lineage(handle), queryFn: ({ signal }) => request('getAgentLineage', { signal, params: { handle } }), staleTime: 60_000 })
}

export const agentStoryQuery = (handle: string, day?: string) =>
  queryOptions({ queryKey: productKeys.story(handle, day ?? ''), queryFn: ({ signal }) => request('getAgentStory', { signal, params: { handle }, query: { day } }), staleTime: 300_000 })

/** `GET /agents/:handle/story?day=`: the agent's story of a UTC day (yesterday when `day` is empty). */
export function useAgentStory(handle: string, day?: string) {
  return useQuery(agentStoryQuery(handle, day))
}

/** `GET /agents/:handle/team`: its trader and scouts, and who did what in its recent decisions. */
export function useAgentTeam(handle: string) {
  return useQuery({ queryKey: productKeys.team(handle), queryFn: ({ signal }) => request('getAgentTeam', { signal, params: { handle } }), staleTime: 60_000 })
}

export const myChallengesQuery = () => queryOptions({ queryKey: productKeys.challenges(), queryFn: ({ signal }) => request('listMyChallenges', { signal }) })

/** `GET /me/challenges`: the challenges the user made or has an agent in. */
export function useMyChallenges(enabled = true) {
  return useQuery({ ...myChallengesQuery(), enabled })
}

export const challengeQuery = (id: string) =>
  queryOptions({ queryKey: productKeys.challenge(id), queryFn: ({ signal }) => request('getChallenge', { signal, params: { id } }), refetchInterval: 300_000 })

/** `GET /challenges/:id`: the board -- each agent's week, costs, worst drop, days sat out and return. */
export function useChallenge(id: string) {
  return useQuery(challengeQuery(id))
}
